/**
 * Migration script to fix all movie IDs in Firestore
 * This script:
 * 1. Finds all documents with double-prefixed IDs (e.g., tmdb_tmdb_XXX)
 * 2. Migrates data to the correct document ID
 * 3. Deletes the old documents
 *
 * Run from browser console: window.migrateAllMovieIds()
 */

import {
  getDb,
  collection,
  getDocs,
  doc as firestoreDoc,
  setDoc,
  deleteDoc,
  query,
  where,
} from '@watchlist/shared';
import { migrateMovieId } from './movieUtils';

interface MigrationResult {
  success: boolean;
  totalDocuments: number;
  migratedDocuments: number;
  deletedDocuments: number;
  errors: string[];
  details: Array<{
    oldId: string;
    newId: string;
    action: 'migrated' | 'merged' | 'skipped' | 'error';
    reason?: string;
  }>;
}

/**
 * Migrate all userMovies documents to fix double-prefixed IDs
 */
export const migrateUserMoviesCollection = async (userId?: string): Promise<MigrationResult> => {
  console.log('🔄 Starting userMovies migration...');

  const result: MigrationResult = {
    success: true,
    totalDocuments: 0,
    migratedDocuments: 0,
    deletedDocuments: 0,
    errors: [],
    details: [],
  };

  try {
    const db = getDb();
    const userMoviesRef = collection(db, 'userMovies');

    // Get all documents (or filter by userId if provided)
    let querySnapshot;
    if (userId) {
      const q = query(userMoviesRef, where('uid', '==', userId));
      querySnapshot = await getDocs(q);
    } else {
      querySnapshot = await getDocs(userMoviesRef);
    }

    console.log(`📊 Found ${querySnapshot.size} documents to check`);
    result.totalDocuments = querySnapshot.size;

    // Group documents by their normalized ID to handle merges
    const documentsByNormalizedId = new Map<string, Array<{
      docId: string;
      data: any;
      originalMovieId: string;
    }>>();

    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const docId = docSnapshot.id;
      const originalMovieId = data.movieId || '';
      const uid = data.uid || '';

      // Normalize the movie ID
      const normalizedMovieId = migrateMovieId(originalMovieId);
      const normalizedDocId = `${uid}_${normalizedMovieId}`;

      if (!documentsByNormalizedId.has(normalizedDocId)) {
        documentsByNormalizedId.set(normalizedDocId, []);
      }
      documentsByNormalizedId.get(normalizedDocId)!.push({
        docId,
        data,
        originalMovieId,
      });
    });

    console.log(`📊 Grouped into ${documentsByNormalizedId.size} unique normalized IDs`);

    // Process each group
    for (const [normalizedDocId, documents] of Array.from(documentsByNormalizedId.entries())) {
      // Extract uid and movieId from normalizedDocId
      const underscoreIndex = normalizedDocId.indexOf('_');
      const normalizedMovieId = normalizedDocId.substring(underscoreIndex + 1);

      if (documents.length === 1) {
        const docEntry = documents[0];

        // Check if this document already has the correct ID
        if (docEntry.docId === normalizedDocId && docEntry.originalMovieId === normalizedMovieId) {
          result.details.push({
            oldId: docEntry.docId,
            newId: normalizedDocId,
            action: 'skipped',
            reason: 'Already has correct ID',
          });
          continue;
        }

        // Single document that needs ID fix
        if (docEntry.originalMovieId !== normalizedMovieId) {
          try {
            // Create new document with correct ID
            const newDocRef = firestoreDoc(db, 'userMovies', normalizedDocId);
            const updatedData = {
              ...docEntry.data,
              movieId: normalizedMovieId,
            };
            await setDoc(newDocRef, updatedData);

            // Delete old document if it has a different ID
            if (docEntry.docId !== normalizedDocId) {
              const oldDocRef = firestoreDoc(db, 'userMovies', docEntry.docId);
              await deleteDoc(oldDocRef);
              result.deletedDocuments++;
            }

            result.migratedDocuments++;
            result.details.push({
              oldId: docEntry.docId,
              newId: normalizedDocId,
              action: 'migrated',
            });
            console.log(`✅ Migrated: ${docEntry.docId} → ${normalizedDocId}`);
          } catch (error: any) {
            result.errors.push(`Error migrating ${docEntry.docId}: ${error.message}`);
            result.details.push({
              oldId: docEntry.docId,
              newId: normalizedDocId,
              action: 'error',
              reason: error.message,
            });
          }
        }
      } else {
        // Multiple documents with same normalized ID - need to merge
        console.log(`🔀 Merging ${documents.length} documents into ${normalizedDocId}`);

        // Score each document to find the "best" one
        const scoredDocs = documents.map((docEntry) => {
          let score = 0;
          const data = docEntry.data;

          // Prefer documents that already have the correct ID format
          if (docEntry.docId === normalizedDocId) score += 100;
          if (docEntry.originalMovieId === normalizedMovieId) score += 50;

          // Score based on data completeness
          if (data.isWatched) score += 20;
          if (data.rating) score += 15;
          if (data.review) score += 15;
          if (data.isInWatchlist) score += 10;
          if (data.watchedDate) score += 5;
          if (data.isFavorite) score += 5;

          // Prefer newer documents
          if (data.updatedAt) {
            const updatedAt = data.updatedAt.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt);
            score += Math.floor(updatedAt.getTime() / (1000 * 60 * 60 * 24)); // Days since epoch
          }

          return { docEntry, score };
        });

        // Sort by score descending
        scoredDocs.sort((a, b) => b.score - a.score);

        // Merge all data into the best document
        const bestDoc = scoredDocs[0].docEntry;
        const mergedData: any = { ...bestDoc.data };

        // Merge data from other documents (prefer non-null/non-undefined values)
        for (let i = 1; i < scoredDocs.length; i++) {
          const otherData = scoredDocs[i].docEntry.data;

          // Only merge values that are "better" than what we have
          if (!mergedData.isWatched && otherData.isWatched) mergedData.isWatched = true;
          if (!mergedData.rating && otherData.rating) mergedData.rating = otherData.rating;
          if (!mergedData.review && otherData.review) mergedData.review = otherData.review;
          if (!mergedData.isInWatchlist && otherData.isInWatchlist) mergedData.isInWatchlist = true;
          if (!mergedData.isFavorite && otherData.isFavorite) mergedData.isFavorite = true;
          if (!mergedData.watchedDate && otherData.watchedDate) mergedData.watchedDate = otherData.watchedDate;
          if (!mergedData.watchCount && otherData.watchCount) mergedData.watchCount = otherData.watchCount;
          if (otherData.watchCount > (mergedData.watchCount || 0)) mergedData.watchCount = otherData.watchCount;
        }

        // Update the movieId to the normalized version
        mergedData.movieId = normalizedMovieId;

        try {
          // Create/update the canonical document
          const newDocRef = firestoreDoc(db, 'userMovies', normalizedDocId);
          await setDoc(newDocRef, mergedData);

          // Delete all other documents
          for (const scoredDoc of scoredDocs) {
            if (scoredDoc.docEntry.docId !== normalizedDocId) {
              const oldDocRef = firestoreDoc(db, 'userMovies', scoredDoc.docEntry.docId);
              await deleteDoc(oldDocRef);
              result.deletedDocuments++;
            }
          }

          result.migratedDocuments++;
          result.details.push({
            oldId: documents.map(d => d.docId).join(', '),
            newId: normalizedDocId,
            action: 'merged',
            reason: `Merged ${documents.length} documents`,
          });
          console.log(`✅ Merged ${documents.length} documents into ${normalizedDocId}`);
        } catch (error: any) {
          result.errors.push(`Error merging documents into ${normalizedDocId}: ${error.message}`);
          result.details.push({
            oldId: documents.map(d => d.docId).join(', '),
            newId: normalizedDocId,
            action: 'error',
            reason: error.message,
          });
        }
      }
    }

    console.log('✅ userMovies migration completed');
  } catch (error: any) {
    result.success = false;
    result.errors.push(`Fatal error: ${error.message}`);
    console.error('❌ Migration failed:', error);
  }

  return result;
};

/**
 * Migrate movieDetails arrays in all watchlists
 */
export const migrateWatchlistsCollection = async (userId?: string): Promise<MigrationResult> => {
  console.log('🔄 Starting watchlists migration...');

  const result: MigrationResult = {
    success: true,
    totalDocuments: 0,
    migratedDocuments: 0,
    deletedDocuments: 0,
    errors: [],
    details: [],
  };

  try {
    const db = getDb();
    const watchlistsRef = collection(db, 'watchlists');

    // Get all watchlists (or filter by userId if provided)
    let querySnapshot;
    if (userId) {
      const q = query(watchlistsRef, where('userId', '==', userId));
      querySnapshot = await getDocs(q);
    } else {
      querySnapshot = await getDocs(watchlistsRef);
    }

    console.log(`📊 Found ${querySnapshot.size} watchlists to check`);
    result.totalDocuments = querySnapshot.size;

    for (const docSnapshot of querySnapshot.docs) {
      const watchlistId = docSnapshot.id;
      const data = docSnapshot.data();
      const movieDetails = data.movieDetails || [];

      if (movieDetails.length === 0) {
        result.details.push({
          oldId: watchlistId,
          newId: watchlistId,
          action: 'skipped',
          reason: 'No movieDetails',
        });
        continue;
      }

      // Check and fix movie IDs in movieDetails
      let hasChanges = false;
      const updatedMovieDetails = movieDetails.map((movie: any) => {
        const originalId = movie.id || movie.imdbID || '';
        const normalizedId = migrateMovieId(originalId);

        if (originalId !== normalizedId) {
          hasChanges = true;
          console.log(`  📝 Fixing movie ID in watchlist ${watchlistId}: ${originalId} → ${normalizedId}`);
          return {
            ...movie,
            id: normalizedId,
            imdbID: normalizedId,
          };
        }
        return movie;
      });

      if (hasChanges) {
        try {
          const watchlistRef = firestoreDoc(db, 'watchlists', watchlistId);
          await setDoc(watchlistRef, {
            ...data,
            movieDetails: updatedMovieDetails,
          }, { merge: true });

          result.migratedDocuments++;
          result.details.push({
            oldId: watchlistId,
            newId: watchlistId,
            action: 'migrated',
            reason: 'Fixed movie IDs in movieDetails',
          });
          console.log(`✅ Updated watchlist: ${watchlistId}`);
        } catch (error: any) {
          result.errors.push(`Error updating watchlist ${watchlistId}: ${error.message}`);
          result.details.push({
            oldId: watchlistId,
            newId: watchlistId,
            action: 'error',
            reason: error.message,
          });
        }
      } else {
        result.details.push({
          oldId: watchlistId,
          newId: watchlistId,
          action: 'skipped',
          reason: 'No ID changes needed',
        });
      }
    }

    console.log('✅ Watchlists migration completed');
  } catch (error: any) {
    result.success = false;
    result.errors.push(`Fatal error: ${error.message}`);
    console.error('❌ Migration failed:', error);
  }

  return result;
};

/**
 * Run the full migration - fixes both userMovies and watchlists collections
 */
export const migrateAllMovieIds = async (userId?: string): Promise<{
  userMovies: MigrationResult;
  watchlists: MigrationResult;
  summary: {
    totalDocumentsProcessed: number;
    totalMigrated: number;
    totalDeleted: number;
    totalErrors: number;
  };
}> => {
  console.log('🚀 Starting full movie ID migration...');
  console.log(userId ? `  Scope: User ${userId}` : '  Scope: All users');
  console.log('');

  const userMoviesResult = await migrateUserMoviesCollection(userId);
  console.log('');
  const watchlistsResult = await migrateWatchlistsCollection(userId);

  const summary = {
    totalDocumentsProcessed: userMoviesResult.totalDocuments + watchlistsResult.totalDocuments,
    totalMigrated: userMoviesResult.migratedDocuments + watchlistsResult.migratedDocuments,
    totalDeleted: userMoviesResult.deletedDocuments + watchlistsResult.deletedDocuments,
    totalErrors: userMoviesResult.errors.length + watchlistsResult.errors.length,
  };

  console.log('');
  console.log('═══════════════════════════════════════');
  console.log('📊 MIGRATION SUMMARY');
  console.log('═══════════════════════════════════════');
  console.log(`  Documents processed: ${summary.totalDocumentsProcessed}`);
  console.log(`  Documents migrated:  ${summary.totalMigrated}`);
  console.log(`  Documents deleted:   ${summary.totalDeleted}`);
  console.log(`  Errors:              ${summary.totalErrors}`);
  console.log('═══════════════════════════════════════');

  if (summary.totalErrors > 0) {
    console.log('');
    console.log('❌ Errors:');
    userMoviesResult.errors.forEach(err => console.log(`  - [userMovies] ${err}`));
    watchlistsResult.errors.forEach(err => console.log(`  - [watchlists] ${err}`));
  }

  return {
    userMovies: userMoviesResult,
    watchlists: watchlistsResult,
    summary,
  };
};

/**
 * Dry run - analyze what would be migrated without making changes
 */
export const analyzeMovieIdMigration = async (userId?: string): Promise<{
  userMovies: {
    total: number;
    needsMigration: number;
    duplicates: number;
    examples: Array<{ oldId: string; newId: string }>;
  };
  watchlists: {
    total: number;
    needsMigration: number;
    examples: Array<{ watchlistId: string; movieId: string; newId: string }>;
  };
}> => {
  console.log('🔍 Analyzing movie IDs (dry run)...');

  const db = getDb();
  const userMoviesResult = {
    total: 0,
    needsMigration: 0,
    duplicates: 0,
    examples: [] as Array<{ oldId: string; newId: string }>,
  };
  const watchlistsResult = {
    total: 0,
    needsMigration: 0,
    examples: [] as Array<{ watchlistId: string; movieId: string; newId: string }>,
  };

  // Analyze userMovies
  try {
    const userMoviesRef = collection(db, 'userMovies');
    let querySnapshot;
    if (userId) {
      const q = query(userMoviesRef, where('uid', '==', userId));
      querySnapshot = await getDocs(q);
    } else {
      querySnapshot = await getDocs(userMoviesRef);
    }

    userMoviesResult.total = querySnapshot.size;

    const normalizedIds = new Map<string, number>();

    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const originalMovieId = data.movieId || '';
      const normalizedMovieId = migrateMovieId(originalMovieId);
      const uid = data.uid || '';
      const normalizedDocId = `${uid}_${normalizedMovieId}`;

      // Count occurrences of normalized IDs
      normalizedIds.set(normalizedDocId, (normalizedIds.get(normalizedDocId) || 0) + 1);

      if (originalMovieId !== normalizedMovieId) {
        userMoviesResult.needsMigration++;
        if (userMoviesResult.examples.length < 10) {
          userMoviesResult.examples.push({
            oldId: originalMovieId,
            newId: normalizedMovieId,
          });
        }
      }
    });

    // Count duplicates
    for (const [, count] of Array.from(normalizedIds.entries())) {
      if (count > 1) {
        userMoviesResult.duplicates += count - 1;
      }
    }
  } catch (error) {
    console.error('Error analyzing userMovies:', error);
  }

  // Analyze watchlists
  try {
    const watchlistsRef = collection(db, 'watchlists');
    let querySnapshot;
    if (userId) {
      const q = query(watchlistsRef, where('userId', '==', userId));
      querySnapshot = await getDocs(q);
    } else {
      querySnapshot = await getDocs(watchlistsRef);
    }

    watchlistsResult.total = querySnapshot.size;

    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const movieDetails = data.movieDetails || [];
      let needsMigration = false;

      for (const movie of movieDetails) {
        const originalId = movie.id || movie.imdbID || '';
        const normalizedId = migrateMovieId(originalId);

        if (originalId !== normalizedId) {
          needsMigration = true;
          if (watchlistsResult.examples.length < 10) {
            watchlistsResult.examples.push({
              watchlistId: docSnapshot.id,
              movieId: originalId,
              newId: normalizedId,
            });
          }
        }
      }

      if (needsMigration) {
        watchlistsResult.needsMigration++;
      }
    });
  } catch (error) {
    console.error('Error analyzing watchlists:', error);
  }

  console.log('');
  console.log('═══════════════════════════════════════');
  console.log('📊 ANALYSIS RESULTS (DRY RUN)');
  console.log('═══════════════════════════════════════');
  console.log('userMovies collection:');
  console.log(`  Total documents:      ${userMoviesResult.total}`);
  console.log(`  Need migration:       ${userMoviesResult.needsMigration}`);
  console.log(`  Duplicate entries:    ${userMoviesResult.duplicates}`);
  if (userMoviesResult.examples.length > 0) {
    console.log('  Examples:');
    userMoviesResult.examples.forEach(ex => console.log(`    ${ex.oldId} → ${ex.newId}`));
  }
  console.log('');
  console.log('watchlists collection:');
  console.log(`  Total watchlists:     ${watchlistsResult.total}`);
  console.log(`  Need migration:       ${watchlistsResult.needsMigration}`);
  if (watchlistsResult.examples.length > 0) {
    console.log('  Examples:');
    watchlistsResult.examples.forEach(ex =>
      console.log(`    [${ex.watchlistId}] ${ex.movieId} → ${ex.newId}`)
    );
  }
  console.log('═══════════════════════════════════════');

  return {
    userMovies: userMoviesResult,
    watchlists: watchlistsResult,
  };
};

// Expose functions globally for browser console access
if (typeof window !== 'undefined') {
  (window as any).migrateAllMovieIds = migrateAllMovieIds;
  (window as any).migrateUserMoviesCollection = migrateUserMoviesCollection;
  (window as any).migrateWatchlistsCollection = migrateWatchlistsCollection;
  (window as any).analyzeMovieIdMigration = analyzeMovieIdMigration;
}
